import { fieldFilter, queryFilter } from 'utils/proptypes';
import base from './baseApi';
import { omitBy } from 'lodash';
import { createSearchParams } from 'react-router';

const { urls, methods, execute } = base();

const getOrganizationByFilter = (queryFilter: queryFilter) => {
   const method = methods.GET;
   const initParams: any = omitBy(queryFilter, v => v === '');
   const params = createSearchParams(initParams);
   const url = `${urls.organization.organizations}/?${params.toString()}`;
   const response = execute(method, url, { authorization: true });

   return response;
};

const getOrganizations = (params?: fieldFilter) => {
   const method = methods.GET;
   const url = `${urls.organization.organizations}?limit=0${params?.fields?.length && `&${params?.fields.map(field => `fields[]=${field}`).join('&')}`}`;
   const response = execute(
      method,
      url,
      { authorization: true },
      // hasChildren is only sent when asked for, so existing callers keep the
      // exact query string they had.
      omitBy({ hasChildren: params?.hasChildren }, v => v === undefined),
   );

   return response;
};

const getOrganizationById = (id: string) => {
   const method = methods.GET;
   const url = `${urls.organization.organizations}/${id}`;

   return execute(method, url, { authorization: true });
};

const createOrganization = (payload: any) => {
   const method = methods.POST;
   const url = `${urls.organization.organizations}`;

   return execute(method, url, { authorization: true }, payload);
};

const updateOrganizationById = (payload: any) => {
   const method = methods.PUT;
   const url = `${urls.organization.organizations}/${payload.id}`;

   return execute(method, url, { authorization: true }, payload);
};

const updatePatchOrganization = ({ id, payload }: any) => {
   const method = methods.PATCH;
   const url = `${urls.organization.organizations}/${id}`;
   return execute(method, url, { authorization: true }, payload);
};

const duplicateOrganization = (id: string) => {
   const method = methods.POST;
   const url = `${urls.organization.organizations}/${id}/duplicate`;

   return execute(method, url, { authorization: true });
};

const updateStatusOrganizationById = (payload: { id: any; isActive: boolean }) => {
   const method = methods.PATCH;
   const url = `${urls.organization.organizations}/${payload.id}`;

   return execute(method, url, { authorization: true }, { isActive: payload.isActive });
};

const updateOrganizationImage = (payload: { id: string; file?: any }) => {
   const { id, file } = payload;
   const method = methods.POST;
   const url = `${urls.organization.organizations}/${id}/uploadimage`;

   return execute(method, url, { authorization: true }, file, true);
};

const setOrgCookie = (id: any) => {
   const method = methods.GET;
   const url = `${urls.organization.organizations}/${id}/set-context`;
   const response = execute(method, url, { authorization: true });

   return response;
};

const confirmAndSaveOrgProfileImage = (payload: { organizationId: string; fileName?: any }) => {
   const method = methods.PUT;
   const url = `${urls.organization.organizations}/confirmAndSaveFile`;
   const response = execute(method, url, { authorization: true }, payload);
   return response;
};

const exportOrganization = (id: string) => {
   const method = methods.GET;
   const url = `${urls.organization.organizations}/${id}/export`;

   return execute(method, url, { authorization: true }, {}, false, {}, 'blob');
};

const preflightImportOrganization = (file: File) => {
   const method = methods.POST;
   const url = `${urls.organization.organizations}/import/preflight`;
   const form = new FormData();
   form.append('file', file);

   return execute(method, url, { authorization: true }, form, true);
};

const commitImportOrganization = (payload: { importToken: string; confirmOverwrite?: boolean }) => {
   const method = methods.POST;
   const url = `${urls.organization.organizations}/import/commit`;

   return execute(method, url, { authorization: true }, payload);
};

const syncAllToActiveCampaign = () => {
   const method = methods.POST;
   const url = `${urls.organization.organizations}/ac-sync`;

   return execute(method, url, { authorization: true });
};

const getOrganizationForTraveler = (queryFilter: queryFilter) => {
   const method = methods.GET;
   const initParams: any = omitBy(queryFilter, v => v === '');
   const params = createSearchParams(initParams);

   const url = `${urls.organization.travelerOrganization}?${params.toString()}`;
   const response = execute(method, url, { authorization: false });

   return response;
};

const getOrganizationDetailBySlug = (slug: string) => {
   const method = methods.GET;

   const url = `${urls.organization.travelerOrganization}/${slug}`;
   const response = execute(method, url, { authorization: false });

   return response;
};

const getOrganizationChapters = (slug: string) => {
   const method = methods.GET;
   const url = `${urls.organization.travelerOrganization}/${slug}/chapters`;

   return execute(method, url, { authorization: false });
};

const setOrganizationParent = (childOrgId: string, parentOrganizationId: string | null) => {
   const method = methods.PUT;
   const url = `${urls.organization.organizations}/${childOrgId}/parent`;

   return execute(method, url, { authorization: true }, { parentOrganizationId });
};

const getEligibleChildren = (orgId: string, query: queryFilter) => {
   const method = methods.GET;
   const initParams: any = omitBy(query, v => v === '' || v === undefined);
   const params = createSearchParams(initParams);
   const url = `${urls.organization.organizations}/${orgId}/eligible-children?${params.toString()}`;

   return execute(method, url, { authorization: true });
};

// NPT-1552: bulk team/chapter provisioning. Preflight accepts a CSV file or
// pasted text (single column of team names) and persists nothing; commit
// creates one inherit-mode chapter org per reviewed row.
const preflightChapters = (orgId: string, input: { file?: File; content?: string }) => {
   const method = methods.POST;
   const url = `${urls.organization.organizations}/${orgId}/chapters/preflight`;
   const form = new FormData();
   if (input.file) form.append('file', input.file);
   if (input.content) form.append('content', input.content);

   return execute(method, url, { authorization: true }, form, true);
};

const commitChapters = (orgId: string, rows: { name: string }[]) => {
   const method = methods.POST;
   const url = `${urls.organization.organizations}/${orgId}/chapters/commit`;

   return execute(method, url, { authorization: true }, { rows });
};

const getEligibleParents = (orgId: string, query: queryFilter) => {
   const method = methods.GET;
   const initParams: any = omitBy(query, v => v === '' || v === undefined);
   const params = createSearchParams(initParams);
   const url = `${urls.organization.organizations}/${orgId}/eligible-parents?${params.toString()}`;

   return execute(method, url, { authorization: true });
};

export type ProceedsReportParams = {
   from?: string;
   to?: string;
   /** Which date the period filters on: purchase (AR) or travel end (AP). */
   basis?: 'purchase' | 'travel';
   /** Asks the API for the per-program booking lines the program tabs render. */
   includeBookings?: boolean;
};

/** NPT-1551 — club-level proceeds roll-up for a parent organization. */
const getOrganizationProceedsReport = (orgId: string, params?: ProceedsReportParams) => {
   const method = methods.GET;
   const url = `${urls.organization.organizations}/${orgId}/proceeds-report`;

   return execute(method, url, { authorization: true }, omitBy(params ?? {}, v => !v));
};

export type ProceedsReportBookingsParams = ProceedsReportParams & {
   /** Program to scope the lines to; "unassigned" for bookings outside any program. */
   programId?: string;
   page?: number;
   limit?: number;
   searchQuery?: string;
   /** Only the Booking column is sortable in the design; omit to use the API's default order. */
   sortBy?: 'booking';
   sortOrder?: 'ASC' | 'DESC';
};

export type ProceedsReportTeamsParams = ProceedsReportParams & {
   /** Omit for the whole club; "unassigned" for teams outside any program. */
   programId?: string;
   page?: number;
   limit?: number;
   /** Only the Team column is sortable in the design; omit to use the API's default order. */
   sortBy?: 'team';
   sortOrder?: 'ASC' | 'DESC';
};

/** NPT-1685 — paginated payout-by-team lines for Overview or one program tab. */
const getProceedsReportTeams = (orgId: string, params: ProceedsReportTeamsParams) => {
   const method = methods.GET;
   const url = `${urls.organization.organizations}/${orgId}/proceeds-report/teams`;

   return execute(method, url, { authorization: true }, omitBy(params, v => v === undefined || v === ''));
};

/** NPT-1685 — paginated booking lines behind a program tab. */
const getProceedsReportBookings = (orgId: string, params: ProceedsReportBookingsParams) => {
   const method = methods.GET;
   const url = `${urls.organization.organizations}/${orgId}/proceeds-report/bookings`;

   return execute(method, url, { authorization: true }, omitBy(params, v => v === undefined || v === ""));
};

/** NPT-1551 / NPT-1686 — downloads the proceeds statement (csv, xlsx or pdf) and saves it via the browser. */
const exportProceedsReportFile = async (orgId: string, params: ProceedsReportParams & { format: 'csv' | 'xlsx' | 'pdf' }) => {
   const url = `${urls.organization.organizations}/${orgId}/proceeds-report/download`;
   const response = await execute(methods.GET, url, { authorization: true }, omitBy(params, v => !v), undefined, undefined, 'blob');

   const blob = response.data instanceof Blob ? response.data : new Blob([response.data]);
   const disposition: string = response.headers?.['content-disposition'] || '';
   const filenameMatch = disposition.match(/filename="?([^"]+)"?/);
   const fileName = filenameMatch?.[1] || `proceeds-report.${params.format}`;

   const objectUrl = URL.createObjectURL(blob);
   const link = document.createElement('a');
   link.href = objectUrl;
   link.download = fileName;
   document.body.appendChild(link);
   link.click();
   document.body.removeChild(link);
   URL.revokeObjectURL(objectUrl);
};

const getOrgHeroPhotoByOrgSlugAndProgramSlug = (orgSlug: string, programSlug?: string) => {
   const method = methods.GET;

   const url = programSlug ? `${urls.organization.travelerOrganization}/${orgSlug}/images?program=${programSlug}` : `${urls.organization.travelerOrganization}/${orgSlug}/images`;
   const response = execute(method, url, { authorization: false });

   return response;
};

export {
   getOrganizationByFilter,
   getOrganizationById,
   updateOrganizationById,
   getOrganizations,
   createOrganization,
   duplicateOrganization,
   updateStatusOrganizationById,
   setOrgCookie,
   updateOrganizationImage,
   confirmAndSaveOrgProfileImage,
   getOrganizationForTraveler,
   getOrganizationDetailBySlug,
   updatePatchOrganization,
   exportOrganization,
   preflightImportOrganization,
   commitImportOrganization,
   syncAllToActiveCampaign,
   getOrganizationChapters,
   preflightChapters,
   commitChapters,
   setOrganizationParent,
   getEligibleChildren,
   getEligibleParents,
   getOrganizationProceedsReport,
   getProceedsReportBookings,
   getProceedsReportTeams,
   exportProceedsReportFile,
   getOrgHeroPhotoByOrgSlugAndProgramSlug
};
